import React from 'react';
import { Link } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import PropTypes from 'prop-types';
import VisuallyHidden from 'components/consumer/VisuallyHidden';
import JBImage from 'components/essentials/JBImage';
import CtaLink from 'components/essentials/Cta/CtaLink';
import addDomainToPath from 'commons/addDomainToPath';
import IconArrowRightSimple from 'components/icons/iconArrowRightSimple';
import ShopByTitleNavigation from 'components/consumer/ShopByTitleNavigation';
import useRandomId from 'hooks/useRandomId';
import useScreen from 'hooks/useScreen';
import useIsBrowser from 'hooks/useIsBrowser';

const sections = [
  {
    id: 'midcentury',
    title: 'Mid Century Modern',
    subTitle: 'Clean lines with 50s & 60s retro charm',
    images: [
      addDomainToPath('product_hero/8501/Chelsea-Sofa-Essence-Ash-T2-017.jpg'),
      addDomainToPath(
        'product_hero/1054/Eliot-Leather-Sofa-Santiago-Camel-T2-129.jpg'
      ),
      addDomainToPath(
        'product_hero/1007/Hughes-Leather-Sofa-Santiago-Steel-T2-103.jpg'
      ),
    ],
  },
  {
    id: 'transitional',
    title: 'Transitional',
    subTitle: 'Classic silhouettes with a contemporary feel',
    link: '/sofas-and-sectionals?style=transitional',
    images: [
      addDomainToPath('product_hero/6468/Hopson-Sofa-Key-Largo-Ash-T3-155.jpg'),
      addDomainToPath('product_hero/621/Hughes-Chair-Lucky-Divine-T2-049.jpg'),
      addDomainToPath(
        'product_hero/8264/Braxton-Sleeper-Sofa-Royale-Evergreen-T2-063.jpg'
      ),
    ],
  },
  {
    id: 'modern',
    title: 'Modern',
    subTitle: 'Focused on simplicity, a smooth and sleek look',
    link: '/sofas-and-sectionals?style=modern',
    images: [
      addDomainToPath('product_hero/9514/Lewis-Sofa-Merit-Dove-T3-054.jpg'),
      addDomainToPath(
        'product_hero/9704/Vaughn-76-Sofa-Palermo-Coffee-T2-068-x.jpg'
      ),
      addDomainToPath(
        'product_hero/661/Stowe-Sofa-Dawson-Slate-T2-044-Flip.jpg'
      ),
    ],
  },
];

const categories = [
  {
    text: 'Sofas',
    link: '/sofas',
    img: addDomainToPath(
      'configurations/pid_8416/-CF437-WS04/8416-CF437-WS04-hughes-grand-sofa-milo-french-blue-t1-1_t.png'
    ),
  },
  {
    text: 'Sleeper Sofas',
    link: '/sleeper-sofas',
    img: addDomainToPath(
      'configurations/pid_8696/-CF033-WS03/8696-CF033-WS03-hughes-sleeper-sofa-bentley-daisey-t1-1_t.png'
    ),
  },
  {
    text: 'Sectional Sofas',
    link: '/sofas-and-sectionals',
    img: addDomainToPath(
      'configurations/pid_9511/-CF387-WS03/9511-CF387-WS03-lewis-sectional-royale-blush-t1-1_t.png'
    ),
  },
  {
    text: 'Loveseats',
    link: '/loveseats',
    img: addDomainToPath(
      'configurations/pid_1009/-CF014-WS03/1009-CF014-WS03-hughes-loveseat-cordova-picante-t1-1_t.png'
    ),
  },
  {
    text: 'Chairs',
    link: '/chairs',
    img: addDomainToPath(
      'configurations/pid_621/-CF412-WS03/621-CF412-WS03-hughes-chair-synergy-pewter-t1-2_t.png'
    ),
  },
  {
    text: 'Beds',
    link: '/beds',
    img: addDomainToPath(
      'configurations/pid_9543/-CF261-WS03/9543-CF261-WS03-alvin-storage-bed-dawson-brindle-t1-2_t.png'
    ),
  },
];

const CategoriesList = ({ sm, section }) => (
  <ul className="min-w-[16rem] ml-20 list-none max-[1023px]:w-full max-[1023px]:ml-0 max-[1023px]:mb-16">
    {categories.map(category => (
      <li
        key={category.link}
        className="[border-bottom:1px_solid_#f1f1f1] [&_a]:text-gray [&_a]:p-2 [&_a]:inline-block [&_a]:w-full [&_a:hover]:text-brand max-[767px]:pl-5 max-[767px]:pr-5 max-[1023px]:pl-[3.333vw] max-[1023px]:pr-[3.333vw] max-[1023px]:flex max-[1023px]:items-center max-[1023px]:justify-between"
      >
        <div className="flex items-center flex-1 [&_img]:mr-2">
          <Link to={`${category.link}?style=${section.id}`}>
            {category.text}
          </Link>
        </div>

        {sm && (
          <div className="w-[.9rem] h-[.9rem] leading-[.9rem] -mt-0.5 flex justify-center items-center [&_img]:h-full">
            <IconArrowRightSimple />
          </div>
        )}
      </li>
    ))}
  </ul>
);

const ShopGroup = ({ sm, section }) => {
  const titleId = useRandomId('shop-by-style-page__group-title');
  const shopAllLinkId = useRandomId('shop-by-style-page__shop-all-link');
  const inspirationLinkId = useRandomId('shop-by-style-page__inspiration-link');

  return (
    <div className="flex-[0.4]">
      <div className="mb-20 max-[767px]:px-5 max-[767px]:py-4 max-[1023px]:px-[3.333vw] max-[1023px]:py-4 max-[1023px]:mb-4">
        <h2
          className="text-3xl font-bold text-gray mt-20 max-[1023px]:mt-0"
          id={titleId}
        >
          {section.title}
        </h2>

        <div className="text-base font-bold mb-6 max-[1023px]:mt-1 max-[1023px]:mb-5 max-[1023px]:text-gray">
          {section.subTitle}
        </div>

        <CtaLink
          aria-labelledby={`${shopAllLinkId} ${titleId}`}
          color="brand"
          id={shopAllLinkId}
          size="base"
          styling="solid-button"
          to={`/sofas-and-sectionals?style=${section.id}`}
        >
          Shop All
          <VisuallyHidden>{` ${section.title}`}</VisuallyHidden>
        </CtaLink>
      </div>

      <div className="flex">
        {!sm && (
          <div>
            <CtaLink
              aria-labelledby={`${inspirationLinkId} ${titleId}`}
              color="brand"
              id={inspirationLinkId}
              noLinkPadding
              size="lg"
              to={`/customer-photos?style=${section.id}`}
            >
              <VisuallyHidden>{`${section.title}: `}</VisuallyHidden>
              Get Inspiration
            </CtaLink>
          </div>
        )}

        <CategoriesList sm={sm} section={section} />
      </div>
    </div>
  );
};

const ImageGrid = ({ images }) => {
  const isBrowser = useIsBrowser();
  const isXsMobileDevice = useMediaQuery({ query: '(max-width: 500px)' });

  // Assume this is an xs mobile device by default, until mounted in browser
  const isXsMobile = isBrowser ? isXsMobileDevice : true;

  const { isMobileOrTablet } = useScreen();
  const imgHeight = isXsMobile ? 300 : isMobileOrTablet ? 450 : 700;

  return (
    <div className="flex-[0.6] flex [&_img]:object-cover">
      <div className="flex-1 pr-2 max-[1023px]:pr-1">
        <JBImage alt="" height={imgHeight} src={images[0]} />
      </div>

      <div className="flex-1 flex flex-col pl-2 max-[1023px]:pl-1 [&_img:first-child]:pb-2 [&_img:first-child]:max-[1023px]:pb-1 [&_img:last-child]:pt-2 [&_img:last-child]:max-[1023px]:pt-1">
        <JBImage alt="" height={imgHeight / 2} src={images[1]} />
        <JBImage alt="" height={imgHeight / 2} src={images[2]} />
      </div>
    </div>
  );
};

const ShopByStyle = () => {
  const { isMobileOrTablet } = useScreen();

  return (
    <div className="shop-by-style-page">
      <ShopByTitleNavigation page="Style" />

      {sections.map(section => (
        <div className="flex mx-[3.333vw] my-0 px-0 py-8 [&:not(:last-child)]:[border-bottom:1px_solid_#f1f1f1] max-[1023px]:m-0 max-[1023px]:p-0 max-[1023px]:flex-col-reverse">
          <ShopGroup section={section} sm={isMobileOrTablet} />
          <ImageGrid images={section.images} />
        </div>
      ))}
    </div>
  );
};

ShopGroup.propTypes = {
  sm: PropTypes.string,
  section: PropTypes.object,
};
CategoriesList.propTypes = {
  sm: PropTypes.string,
  section: PropTypes.object,
};

ImageGrid.propTypes = {
  images: PropTypes.array,
};

export default ShopByStyle;
